<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { TBadgeVariant } from '../../atom/Badge/interfaces'
import { useMenuStore } from '~/stores/menu'
import type { Maybe, Sg_Container, Sg_Item } from '~/schema/types/generated'

const { categories } = defineProps<Props>()
const brandSectionItem = ref<Sg_Item>()
const isBrandSectionFullWidth = ref<boolean>(false)

const store = useMenuStore()
const { brandSectionData } = storeToRefs(store)

interface Props {
    categories: Maybe<Maybe<Sg_Container>[]> | undefined
    catTitle: string
}

const isLibertyPopupOpen = ref(false)

const { sendMenuEvent } = useSendEvent()

onMounted(() => {
    categories?.forEach((cat) => {
        const item = cat?.sg_items?.find((item) => item?.brandSection)
        if (item) {
            brandSectionItem.value = item
        }
    })

    // display brand section full width or regular menu category width (1/3)
    // brand section is full width when ammount of categories (besides of brand section) % 3
    // max ammount of items in row is 3
    if (categories?.length) {
        isBrandSectionFullWidth.value = (categories.length - 1) % 3 === 0
    }
})

const goToExternalLink = (link: string) => navigateTo(link, { external: true })
</script>

<template>
    <div class="hidden md:block">
        <div class="grid grid-cols-3 gap-x-4 gap-y-10">
            <div
                v-for="(cat, idx) in categories"
                :key="`menucat-${idx}`"
            >
                <ul>
                    <li
                        v-for="(item, itemIdx) in cat?.sg_items"
                        :key="item?.title || itemIdx"
                    >
                        <PlusBlogLpMoleculeMenuCloseItemDesktop
                            v-if="item?.libertyPopup"
                            @select="
                                (e) => {
                                    e.preventDefault()
                                    isLibertyPopupOpen = true
                                }
                            "
                        >
                            <PlusBlogLpMoleculeMenuLibertyPopup
                                v-model:open="isLibertyPopupOpen"
                                tabindex="0"
                                @close="isLibertyPopupOpen = false"
                            >
                                <template #trigger>
                                    <PlusBlogLpAtomMenubarIconTitle
                                        :id="item.title"
                                        class="!cursor-pointer py-3 focus-visible:outline focus-visible:outline-1 focus-visible:outline-black"
                                        :icon="item.icon"
                                        :title="item.title"
                                        @click="sendMenuEvent(`${catTitle} > ${item.title}`, item?.url ?? '')"
                                    />
                                </template>
                            </PlusBlogLpMoleculeMenuLibertyPopup>
                        </PlusBlogLpMoleculeMenuCloseItemDesktop>

                        <PlusBlogLpMoleculeMenuCloseItemDesktop
                            v-else-if="item?.url && !item.brandSection"
                            @select="goToExternalLink(item?.url)"
                        >
                            <a :href="item.url">
                                <PlusBlogLpAtomMenubarIconTitle
                                    :id="item.title"
                                    class="!cursor-pointer py-3"
                                    :icon="item.icon"
                                    :title="item.title"
                                />
                            </a>
                        </PlusBlogLpMoleculeMenuCloseItemDesktop>

                        <PlusBlogLpAtomMenubarIconTitle
                            v-else-if="item?.brandSection ? !isBrandSectionFullWidth : true"
                            :id="item?.title"
                            class="py-3"
                            :icon="item?.icon"
                            :title="item?.title"
                        />

                        <ul
                            v-if="item?.sg_links?.length"
                            class="ml-8"
                        >
                            <li
                                v-for="(link, linkIdx) in item.sg_links"
                                :key="link?.title || linkIdx"
                                class="py-[6px] text-lp-paragraph-lg"
                            >
                                <PlusBlogLpMoleculeMenuCloseItemDesktop @select="goToExternalLink(link?.url ?? '')">
                                    <h4 class="flex items-center gap-2 font-figtree text-lp-paragraph-lg">
                                        <PlusBlogLpAtomLink
                                            v-if="link?.url"
                                            :link="link.url"
                                            :value="link?.title ?? ''"
                                            class="!w-auto"
                                        />
                                        <PlusBlogLpAtomBadge
                                            v-if="link?.badgeText"
                                            :variant="(link?.badgeVariant as unknown as TBadgeVariant) ?? 'blue'"
                                        >
                                            {{ link.badgeText }}
                                        </PlusBlogLpAtomBadge>
                                    </h4>
                                </PlusBlogLpMoleculeMenuCloseItemDesktop>
                            </li>
                        </ul>

                        <PlusBlogLpMoleculeMenuBrandSection
                            v-if="item?.brandSection && brandSectionData && !isBrandSectionFullWidth"
                            :brands="brandSectionData"
                            :sub-cat-title="item.title ?? ''"
                            :cat-title="catTitle"
                            class="my-2"
                        />
                    </li>
                </ul>
            </div>
        </div>

        <div v-if="brandSectionItem && brandSectionData && isBrandSectionFullWidth">
            <PlusBlogLpAtomMenubarIconTitle
                :id="brandSectionItem.title"
                class="py-3"
                :icon="brandSectionItem.icon"
                :title="brandSectionItem.title"
            />
            <PlusBlogLpMoleculeMenuBrandSection
                :brands="brandSectionData"
                :sub-cat-title="brandSectionItem.title ?? ''"
                :cat-title="catTitle"
                class="my-2"
                display-row
            />
        </div>
    </div>
</template>
